import React, { useEffect, useMemo, useState } from 'react';

const statusStyles = {
  vencido: 'bg-rose-100 text-rose-700',
  programado: 'bg-amber-100 text-amber-700',
  pendiente: 'bg-amber-100 text-amber-700',
  sin_programar: 'bg-slate-100 text-slate-600',
};

const formatDate = (value) => (value ? new Date(value).toLocaleString('es-ES') : 'Sin fecha');

export const ClinicPatientsPanel = ({ workspace, onOpenReport, onSendFollowUpEmail, consultMode, saving, onGenerateAppAccess, appAccess, onCloseAppAccess, onSendWelcomeEmail }) => {
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(60);
  const [welcomeEmail, setWelcomeEmail] = useState('');

  useEffect(() => {
    setWelcomeEmail(appAccess?.patient_email || '');
  }, [appAccess]);

  const copyShareMessage = async () => {
    try {
      await navigator.clipboard.writeText(appAccess.share_message);
    } catch (error) {
      window.prompt('Copia el mensaje:', appAccess.share_message);
    }
  };

  const filteredPatients = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return workspace.patient_crm || [];
    return (workspace.patient_crm || []).filter((patient) => [
      patient.patient_name,
      patient.patient_email,
      patient.patient_phone,
      patient.patient_dni,
      patient.primary_site,
      patient.preferred_assistant,
      ...(patient.profile_tags || []),
    ].filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [query, workspace.patient_crm]);

  const visiblePatients = filteredPatients.slice(0, visibleCount);

  return (
    <section className="space-y-5" data-testid="clinic-patients-panel">
      {appAccess && (
        <article className="rounded-[28px] border border-teal-200 bg-[#ecfdf5] p-6 shadow-[0_16px_40px_rgba(15,118,110,0.12)]" data-testid="clinic-app-access-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Acceso a la app del paciente</p>
              <p className="mt-2 text-lg font-black text-slate-900" data-testid="clinic-app-access-patient-name">{appAccess.patient_name}</p>
              <p className="mt-1 text-sm text-slate-600">Código de acceso: <span className="rounded-xl bg-white px-3 py-1 text-xl font-black tracking-[0.3em] text-[#0f766e]" data-testid="clinic-app-access-code">{appAccess.code}</span></p>
              <p className="mt-2 text-xs text-slate-500">El paciente entra en <span className="font-semibold">{appAccess.app_url}</span> con su teléfono o DNI + este código.</p>
            </div>
            <button type="button" onClick={onCloseAppAccess} className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-500" data-testid="clinic-app-access-close">Cerrar</button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={copyShareMessage} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white" data-testid="clinic-app-access-copy">Copiar mensaje</button>
            {!!appAccess.patient_phone && (
              <a
                href={`https://wa.me/${(appAccess.patient_phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(appAccess.share_message)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white"
                data-testid="clinic-app-access-whatsapp"
              >
                Enviar por WhatsApp
              </a>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="clinic-app-access-email-row">
            <input
              type="email"
              className="min-w-[240px] flex-1 rounded-full border border-teal-200 bg-white px-4 py-2 text-sm"
              placeholder="email del paciente"
              value={welcomeEmail}
              onChange={(event) => setWelcomeEmail(event.target.value)}
              data-testid="clinic-app-access-email-input"
            />
            <button
              type="button"
              onClick={() => onSendWelcomeEmail(appAccess.patient_key, welcomeEmail)}
              className="rounded-full bg-[#0f766e] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={!welcomeEmail || saving === `welcome-${appAccess.patient_key}`}
              data-testid="clinic-app-access-send-welcome-email"
            >
              {saving === `welcome-${appAccess.patient_key}` ? 'Enviando…' : 'Enviar email de bienvenida'}
            </button>
          </div>
        </article>
      )}
      <div className="grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
        <article className="rounded-[28px] border border-white/60 bg-white/95 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]" data-testid="clinic-patients-summary-card">
          <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">CRM clínico</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Seguimiento del paciente</h2>
          <p className="mt-2 text-sm text-slate-600" data-testid="clinic-patients-friendly-helper">Busca por nombre, teléfono, email o sede. Aquí verás rápido lo último que pasó con cada paciente.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-patients-summary-total">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Pacientes</p>
              <p className="mt-2 text-3xl font-black text-slate-900">{workspace.metrics?.patients ?? 0}</p>
            </div>
            <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-patients-summary-followups">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Seguimientos</p>
              <p className="mt-2 text-3xl font-black text-slate-900">{workspace.metrics?.follow_up_pending ?? 0}</p>
            </div>
            <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid="clinic-patients-summary-mode">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Modo consulta</p>
              <p className="mt-2 text-lg font-black text-slate-900">{consultMode ? 'Tablet táctil' : 'Escritorio completo'}</p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <input
              data-testid="clinic-patient-search-input"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              placeholder="Buscar por nombre, DNI, email, sede, asistente o etiqueta"
              value={query}
              onChange={(event) => { setQuery(event.target.value); setVisibleCount(60); }}
            />
            <p className="text-xs text-slate-500" data-testid="clinic-patients-search-result-count">Mostrando {visiblePatients.length} de {filteredPatients.length} paciente(s)</p>
          </div>
        </article>

        <article className="rounded-[28px] border border-white/60 bg-[#102a43] p-6 text-white shadow-[0_20px_60px_rgba(16,42,67,0.32)]" data-testid="clinic-reports-card">
          <p className="text-xs uppercase tracking-[0.24em] text-cyan-200">Reportes PDF</p>
          <h3 className="mt-2 text-2xl font-black">Exportación clínica</h3>
          <p className="mt-3 text-sm text-cyan-100">Descarga rápida para móvil, tablet o PC: inventario, agenda y cada historial por paciente.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={() => onOpenReport(workspace.report_export_urls?.inventory)} className="rounded-full bg-white px-4 py-3 text-sm font-semibold text-slate-900" data-testid="clinic-report-inventory-button">Exportar inventario</button>
            <button type="button" onClick={() => onOpenReport(workspace.report_export_urls?.agenda)} className="rounded-full bg-cyan-300 px-4 py-3 text-sm font-semibold text-slate-900" data-testid="clinic-report-agenda-button">Exportar agenda</button>
          </div>
        </article>
      </div>

      <div className="grid gap-4 lg:grid-cols-2" data-testid="clinic-patients-list">
        {visiblePatients.length ? visiblePatients.map((patient) => (
          <article key={patient.patient_key} className="rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)]" data-testid={`clinic-patient-card-${patient.patient_key}`}>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-lg font-bold text-slate-900" data-testid={`clinic-patient-name-${patient.patient_key}`}>{patient.patient_name}</p>
                <p className="mt-1 text-sm text-slate-500">{patient.patient_phone || 'sin teléfono'} · {patient.patient_email || 'sin email'}</p>
                <p className="mt-1 text-xs text-slate-400" data-testid={`clinic-patient-dni-${patient.patient_key}`}>DNI {patient.patient_dni || 'sin dato'} · {patient.primary_site || 'sin sede'}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {!!patient.patient_phone && (
                    <a href={`https://wa.me/${(patient.patient_phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${patient.patient_name}, le escribimos de la Clínica de la Dra. Sandra Alvarez.`)}`} target="_blank" rel="noreferrer" className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800 hover:bg-emerald-200" data-testid={`clinic-patient-wa-${patient.patient_key}`}>💬 WhatsApp</a>
                  )}
                  {!!patient.patient_email && (
                    <a href={`mailto:${patient.patient_email}?subject=${encodeURIComponent('Clínica Dra. Sandra Alvarez')}`} className="rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-800 hover:bg-sky-200" data-testid={`clinic-patient-email-${patient.patient_key}`}>✉️ Email</a>
                  )}
                </div>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[patient.follow_up_status] || statusStyles.sin_programar}`} data-testid={`clinic-patient-followup-status-${patient.patient_key}`}>
                {patient.follow_up_status}
              </span>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-patient-next-appointment-${patient.patient_key}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Próxima cita</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{patient.next_appointment_at ? formatDate(patient.next_appointment_at) : 'Sin cita próxima'}</p>
                <p className="mt-1 text-xs text-slate-500">{patient.next_appointment_type || 'Pendiente programar'}</p>
              </div>
              <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-patient-treatment-${patient.patient_key}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Tratamiento</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{patient.treatment_plan || 'Sin plan todavía'}</p>
                <p className="mt-1 text-xs text-slate-500">Seguimiento {patient.follow_up_date || 'no programado'}</p>
              </div>
              <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-patient-last-movement-${patient.patient_key}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Último movimiento</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{patient.latest_movement_summary || 'Sin movimiento cargado'}</p>
                <p className="mt-1 text-xs text-slate-500">{patient.last_movement_at ? formatDate(patient.last_movement_at) : 'Sin fecha histórica'}</p>
              </div>
              <div className="rounded-[22px] bg-[#f8fafc] p-4" data-testid={`clinic-patient-latest-annotation-${patient.patient_key}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Última anotación</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{patient.latest_annotation || 'Sin anotación clínica'}</p>
                <p className="mt-1 text-xs text-slate-500">Sesiones {patient.total_sessions || 0} · movimientos {patient.total_movements || 0}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2" data-testid={`clinic-patient-flags-${patient.patient_key}`}>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${patient.has_canvas ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`clinic-patient-canvas-flag-${patient.patient_key}`}>{patient.has_canvas ? 'dibujo guardado' : 'sin dibujo'}</span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${patient.has_signature ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`clinic-patient-signature-flag-${patient.patient_key}`}>{patient.has_signature ? 'firma guardada' : 'sin firma'}</span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${patient.stock_deduction_status === 'applied' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`clinic-patient-stock-flag-${patient.patient_key}`}>{patient.stock_deduction_status === 'applied' ? 'stock descontado' : 'sin descuento'}</span>
              {!!patient.preferred_assistant && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700" data-testid={`clinic-patient-assistant-${patient.patient_key}`}>{patient.preferred_assistant}</span>}
              {!!patient.preferred_payment_type && <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white" data-testid={`clinic-patient-payment-${patient.patient_key}`}>{patient.preferred_payment_type}</span>}
              {(patient.profile_tags || []).slice(0, 4).map((tag) => <span key={`${patient.patient_key}-${tag}`} className="rounded-full bg-[#e0f2fe] px-3 py-1 text-xs font-semibold text-[#075985]" data-testid={`clinic-patient-tag-${patient.patient_key}-${tag}`}>{tag}</span>)}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" onClick={() => onOpenReport(patient.report_export_url)} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40" data-testid={`clinic-patient-export-${patient.patient_key}`} disabled={!patient.report_export_available}>
                Exportar historial PDF
              </button>
              <button type="button" onClick={() => onSendFollowUpEmail(patient.patient_key)} className="rounded-full bg-[#e0f2fe] px-4 py-2 text-sm font-semibold text-[#075985]" data-testid={`clinic-patient-followup-email-${patient.patient_key}`} disabled={!patient.patient_email || saving === `followup-${patient.patient_key}`}>
                {saving === `followup-${patient.patient_key}` ? 'Enviando…' : 'Enviar seguimiento email'}
              </button>
              <button type="button" onClick={() => onGenerateAppAccess(patient)} className="rounded-full bg-[#ccfbf1] px-4 py-2 text-sm font-semibold text-[#0f766e]" data-testid={`clinic-patient-app-access-${patient.patient_key}`} disabled={saving === `app-access-${patient.patient_key}`}>
                {saving === `app-access-${patient.patient_key}` ? 'Generando…' : 'Acceso app paciente'}
              </button>
            </div>
          </article>
        )) : <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/80 p-8 text-sm text-slate-500" data-testid="clinic-patients-empty">Aún no hay pacientes en el CRM clínico.</div>}
      </div>

      {filteredPatients.length > visibleCount && (
        <div className="flex justify-center">
          <button type="button" onClick={() => setVisibleCount((count) => count + 60)} className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm" data-testid="clinic-patients-load-more-button">
            Mostrar 60 más
          </button>
        </div>
      )}
    </section>
  );
};
